/**
 * GIF anime d'une retouche, encode DANS LE NAVIGATEUR.
 *
 * Le serveur ne voit jamais passer une image : il relaie des traits, il ne fait
 * aucun traitement graphique, et ce n'est pas un export de galerie qui va le
 * lui faire faire.
 *
 * DEPENDANCE ASSUMEE : `gifenc` (MIT, 12 Ko en ESM, aucune dependance).
 * Le navigateur ne sait pas produire de GIF anime : il n'existe pas
 * d'equivalent de `toBlob` pour ce format. L'ecrire a la main demanderait un
 * quantificateur de couleurs, une compression LZW et l'assemblage du conteneur
 * GIF89a, soit plusieurs centaines de lignes dont les bugs ne se voient qu'a
 * l'œil, sur un fichier corrompu. `gifenc` fait exactement cela, et son propre
 * README precise qu'il vise « les graphiques vectoriels plats » : c'est
 * litteralement notre contenu.
 *
 * On n'encode PAS depuis la video : il faudrait decoder du VP8/VP9 avant de
 * reencoder. Les traits sont disponibles en vectoriel, chaque image est donc
 * rendue a la demande, a la taille voulue.
 */
import { GIFEncoder, applyPalette, quantize } from 'gifenc';
import type { PreparedArtwork } from './renderArtwork.js';

/**
 * Cote du GIF, en pixels. Assez pour lire la retouche, assez peu pour que le
 * fichier reste partageable : le GIF ne compresse rien d'une image a l'autre,
 * son poids suit donc la surface multipliee par le nombre d'images.
 */
const SIZE = 480;
/**
 * Taille de la table de couleurs. 256 est le PLAFOND DU FORMAT GIF, pas un
 * reglage : on ne peut pas elargir le spectre au-dela, seulement mieux
 * depenser ces 256 entrees. C'est ce que font `useSqrt: false` et le choix de
 * l'echantillon ci-dessous.
 */
const MAX_GIF_COLORS = 256;
/** Images du trace. 18 sur 1,5 s : le geste se lit, le fichier reste leger. */
const FRAMES = 18;
/** Duree du trace, alignee sur le replay a l'ecran. */
const DRAW_MS = 1500;
/** Pause sur l'image finie avant que la boucle reparte. */
const HOLD_MS = 1400;

/**
 * De quoi la table de couleurs est tiree.
 *
 * PAS de la seule image finale, et c'est la correction qui compte. Une retouche
 * qui recouvre entierement sa zone fait DISPARAITRE de l'image finale la
 * couleur de fond de depart : celle-ci n'entre alors dans aucune entree de la
 * table, alors qu'elle occupe tout le debut de l'animation. Les premieres
 * images se rabattaient sur la teinte la plus proche, ce qui se voit d'autant
 * plus que le fond est un aplat.
 *
 * On echantillonne donc les deux extremites du trace. Pas toutes les images :
 * elles sont dix-neuf, la quantification est l'etape couteuse, et les images
 * intermediaires ne sont qu'un melange des deux bouts.
 */
function paletteSource(paint: (progress: number) => Uint8ClampedArray): Uint8ClampedArray {
  const first = paint(0).slice();
  const last = paint(1);
  const both = new Uint8ClampedArray(first.length + last.length);
  both.set(first, 0);
  both.set(last, first.length);
  return both;
}

/**
 * Rend le trace image par image et l'encode.
 *
 * La palette est calculee UNE FOIS sur l'image finale, puis reappliquee a
 * toutes les autres. Deux raisons : c'est l'etape couteuse, et une palette par
 * image ferait vaciller les couleurs du tableau d'une image a l'autre alors que
 * seule la retouche change.
 */
export async function encodeReplayGif(prepared: PreparedArtwork): Promise<Blob> {
  const canvas = document.createElement('canvas');
  canvas.width = SIZE;
  canvas.height = SIZE;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (ctx === null) throw new Error('contexte 2d indisponible');

  const paint = (progress: number): Uint8ClampedArray => {
    prepared.setProgress(progress);
    prepared.paint(canvas, { size: SIZE, badge: progress >= 1 });
    return ctx.getImageData(0, 0, SIZE, SIZE).data;
  };

  const palette = quantize(paletteSource(paint), MAX_GIF_COLORS, {
    format: 'rgb565',
    // useSqrt attenue le poids des couleurs frequentes. Sur nos illustrations,
    // ou de grands aplats occupent l'essentiel de la surface, cette ponderation
    // COUTE de la fidelite au lieu d'en gagner : mesure sur un tableau reel,
    // l'ecart moyen a l'original passe de 4,14 a 3,49 en la coupant, et de 4,72
    // a 4,09 a l'interieur de la retouche, qui est pourtant la seule chose que
    // ce GIF existe pour montrer.
    useSqrt: false,
  });
  // COMPROMIS mesure : une table plus variee se compresse moins bien. Sur un
  // tableau reel, le fichier passe de 789 a 994 Ko, dont 5 Ko seulement pour
  // l'echantillon a deux extremites. C'est le prix de la fidelite, et il reste
  // tres loin des 7 Mo que le partage accepte.

  const gif = GIFEncoder();
  for (let index = 0; index < FRAMES; index += 1) {
    const progress = (index + 1) / FRAMES;
    // La table de couleurs n'accompagne que la PREMIERE image : elle devient la
    // table globale, les suivantes s'y referent. La repeter a chaque image
    // alourdirait le fichier de 256 couleurs par trame.
    gif.writeFrame(applyPalette(paint(progress), palette), SIZE, SIZE, {
      ...(index === 0 ? { palette } : {}),
      delay: Math.round(DRAW_MS / FRAMES),
      repeat: 0,
    });
    // Rendre la main entre deux images : l'encodage reste sur le fil principal,
    // et sans cette respiration l'interface se figerait le temps du travail.
    await new Promise((resolve) => window.setTimeout(resolve, 0));
  }

  // Derniere image tenue : sans elle, la boucle repart sur le dernier trait et
  // on ne voit jamais le resultat.
  gif.writeFrame(applyPalette(paint(1), palette), SIZE, SIZE, { delay: HOLD_MS });
  gif.finish();

  // `slice()` : le Blob veut un ArrayBuffer, pas la vue typee qui le recouvre.
  return new Blob([gif.bytes().slice().buffer], { type: 'image/gif' });
}
